import formComponent, { getUtmValue } from './form.js';

const getDefaultContactState = () => ({
    conversion_page_url: window.location.href,
    first_name: '',
    last_name: '',
    email: '',
    company: '',
    city: '',
    state: '',
    marketing_opt_in: false,
    operation_type: '',
    units: '',
    primary_fry: '',
    oil_filter_frequency: '',
    estimated_volume: '',
    utm_content: getUtmValue('utm_content'),
    utm_campaign: getUtmValue('utm_campaign'),
    utm_medium: getUtmValue('utm_medium'),
    utm_source: getUtmValue('utm_source'),
});

const STANDARD_MENU_ITEMS = [
    { label: 'Fries/Potatoes', value: 'Fries/Potatoes' },
    { label: 'Chicken', value: 'Chicken' },
    { label: 'Breaded', value: 'Breaded' },
    { label: 'Battered', value: 'Battered' },
    { label: 'Multi-Purpose', value: 'Multi-Purpose' },
];

const BAKERY_MENU_ITEMS = [
    { label: 'Donuts', value: 'Donuts' },
    { label: 'Pies/Pastries', value: 'Pies/Pastries' },
    { label: 'Croissants/Laminated Dough', value: 'Croissants/Laminated Dough' },
    { label: 'Cookies/Baked Goods', value: 'Cookies/Baked Goods' },
    { label: 'Multi-Purpose Bakery', value: 'Multi-Purpose Bakery' },
];

export default function oilSelectorComponent() {
    return {
        // Initialize form handler with custom configuration
        ...formComponent({
            endpoint: '/actions/scp/form/submit',
            formHandle: 'oil-selector',
            minSubmitTime: 3000,
            honeypotFieldName: 'website',
            onSuccess: (result) => {
            },
            onError: (error) => {
                // Handle error in the context of oil selector
                console.error('Oil selector submission failed:', error);
            },
            beforeSubmit: function(payload) {
                return true; // Continue with submission
            }
        }),
        currentIndex: 0,
        answers: {},
        contact: getDefaultContactState(),
        get isBakery() {
            return this.answers.operation_type === 'Bakery';
        },
        get steps() {
            const allSteps = [
                {
                    question: 'What is your operation type?',
                    name: 'operation_type',
                    answers: [
                        { label: 'QSR', value: 'QSR' },
                        { label: 'Fast Casual', value: 'Fast Casual' },
                        { label: 'Casual Dining', value: 'Casual Dining' },
                        { label: 'Upscale Casual', value: 'Upscale Casual' },
                        { label: 'Fine Dining', value: 'Fine Dining' },
                        { label: 'Bakery', value: 'Bakery' },
                        { label: 'Other', value: 'Other' },
                    ],
                },
                {
                    question: 'How many units do you have?',
                    name: 'units',
                    answers: [
                        { label: '0-10', value: '0-10' },
                        { label: '11-30', value: '11-30' },
                        { label: '31-50', value: '31-50' },
                        { label: '50+', value: '50+' },
                    ],
                },
                {
                    question: 'What is your core menu item?',
                    name: 'primary_fry',
                    answers: this.isBakery ? BAKERY_MENU_ITEMS : STANDARD_MENU_ITEMS,
                },
                {
                    question: 'How often do you filter your oil?',
                    name: 'oil_filter_frequency',
                    answers: [
                        { label: 'Weekly', value: 'Weekly' },
                        { label: 'Daily', value: 'Daily' },
                        { label: '2+ Times Per Day', value: '2+ Times Per Day' },
                    ],
                    skip: this.isBakery,
                },
                {
                    question: 'How many cases per week?',
                    name: 'estimated_volume',
                    answers: [
                        { label: 'Less than 200 cases', value: 'Less than 200' },
                        { label: '200 - 400 cases', value: '200-400' },
                        { label: '400 - 800 cases', value: '400-800' },
                        { label: '800+ cases', value: '800+' },
                    ],
                },
            ];

            return allSteps.filter((step) => !step.skip);
        },
        get totalStages() {
            return this.steps.length + 1; // includes email stage
        },
        get progress() {
            const offset = this.showEmailStage || this.submitted ? 1 : 0;
            const total = this.totalStages;
            const clamped = Math.min(this.currentIndex + 1 + offset, total);

            return Math.round((clamped / total) * 100);
        },
        get isQuestionStage() {
            return this.currentIndex < this.steps.length;
        },
        get showEmailStage() {
            return this.currentIndex === this.steps.length;
        },
        get activeStep() {
            return this.steps[this.currentIndex] ?? null;
        },
        get canAdvance() {
            if (this.isQuestionStage) {
                return Boolean(this.answers[this.activeStep?.name]);
            }

            return Boolean(this.contact.first_name && this.contact.last_name && this.contact.email && this.contact.city && this.contact.state && this.contact.company);
        },
        warning: false,
        selectAnswer(value) {
            const step = this.activeStep;
            if (!step) {
                return;
            }

            const previousValue = this.answers[step.name];
            this.answers[step.name] = value;

            // Reset core menu item when operation type changes since the
            // bakery and non-bakery flows offer different answer options.
            if (step.name === 'operation_type' && previousValue !== value) {
                this.answers.primary_fry = '';
            }
        },
        next() {
            if (!this.canAdvance) {
                this.warning = true;
                return;
            }

            this.warning = false;

            const nextIndex = Math.min(this.currentIndex + 1, this.steps.length);
            this.currentIndex = nextIndex;
        },
        previous() {
            this.currentIndex = Math.max(this.currentIndex - 1, 0);
            this.warning = false;
        },
        restart() {
            this.currentIndex = 0;
            this.answers = {};
            this.contact = getDefaultContactState();
            this.submitted = false;
            this.error = null;
            this.success = null;
        },
        serializeResponses() {
            return this.steps.map((step) => ({
                name: step.name,
                question: step.question,
                answer: this.answers[step.name] ?? null
            }));
        },
        answerLabel(stepName) {
            const value = this.answers[stepName];
            const step = this.steps.find((s) => s.name === stepName);

            if (!step || !value) {
                return '';
            }

            const match = step.answers.find((option) => option.value === value);
            return match ? match.label : value;
        },
        get serializedPayload() {
            return JSON.stringify({
                responses: this.serializeResponses(),
                contact: this.contact
            });
        },
        async handleSubmit(event) {
            if (!this.canAdvance || !this.showEmailStage) {
                return;
            }

            // Prepare the form data
            const formData = {
                responses: this.serializeResponses(),
                contact: this.contact
            };

            // Use the form component's submit method
            await this.submit(event, formData);
        }
    };
}

